import React, { useEffect, useState } from "react";
import {
  Paper, Table, TableHead, TableBody, TableCell, TableRow, TableContainer,
  IconButton, Alert, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, FormControl, InputLabel, Select, Typography, Box
} from "@mui/material";
import { Delete, Edit } from "@mui/icons-material";

import UserRegisterDialog from "./UserRegisterDialog";
import {
  getUsers,
  getCompanies,
  getUserCompany,
  assignUserToCompany,
  updateUser,
  deleteUser
} from "../services/api";
import { Title } from "./common/Title";
import { getUserId, getUserRole } from "../services/getUserRole";

/**
 * Admin-only user management area
 */
const UserManagement = ({ token, role }) => {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [error, setError] = useState("");
  const [isRegisterDialogOpen, setRegisterDialogOpen] = useState(false);
  const loggedUserId = getUserId();

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const data = await getUsers(token);
      const companiesList = await getCompanies(token);
      setCompanies(companiesList);

      // If user is "customer," retrieve their linked company
      const usersWithCompany = await Promise.all(
        data.map(async (user) => {
          if (user.role === "customer") {
            try {
              const companyInfo = await getUserCompany(token, user.id);
              return {
                ...user,
                companyId: companyInfo.company_id,
                companyName: companyInfo.company_name
              };
            } catch (err) {
              console.error(`Erro ao buscar empresa do usuário ${user.id}:`, err);
            }
          }
          return { ...user, companyId: null, companyName: "Não atribuído" };
        })
      );
      setUsers(usersWithCompany);
    } catch (err) {
      console.error("Erro no fetchAll:", err);
      setError("Erro ao buscar usuários ou empresas.");
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
  
    try {
      const payload = { ...selectedUser };
      delete payload.newPassword;
  
      const isAdmin = getUserRole() === "admin";
      const isOwnAccount = selectedUser.id === getUserId();
  
      if ((isAdmin || isOwnAccount) && selectedUser.newPassword && selectedUser.newPassword.trim() !== "") {
        payload.password = selectedUser.newPassword;
      }
  
      await updateUser(token, selectedUser.id, payload);
  
      if (selectedUser.role === "customer" && selectedUser.companyId) {
        await assignUserToCompany(token, {
          companyId: selectedUser.companyId,
          userId: selectedUser.id,
          role: "customer",
        });
      }
  
      setSelectedUser(null);
      fetchAll();
    } catch (error) {
      console.error("Erro ao atualizar usuário:", error);
      setError("Erro ao atualizar usuário.");
    }
  };
  

  // const handleDeleteUser = async (id) => {
  //   try {
  //     await deleteUser(token, id);
  //     setDeleteConfirmOpen(false);
  //     fetchAll();
  //   } catch (err) {
  //     setError("Erro ao excluir usuário.");
  //   }
  // };
  const translateRole = (role) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'user':
        return 'Admin Secundário';
      case 'customer':
        return 'Admin Cliente';
      default:
        return role;
    }
  };
  return (
    <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
      <Title>
        🛠️ Gestão de Usuários Config
      </Title>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead sx={{ backgroundColor: "#1976d2" }}>
            <TableRow>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Usuário</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Cargo</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Nome Completo</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Email</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold" }}>Empresa</TableCell>
              <TableCell sx={{ color: "white", fontWeight: "bold", textAlign: "center" }}>
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => ((role === "user" && user.role === "customer")|| (role === "admin") || user.id === loggedUserId) ?(
              
              <TableRow sx={{backgroundColor: user.is_enabled ? "":"rgba(224, 32, 32, 0.1)"}} key={user.id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>{translateRole(user.role)}</TableCell>
                <TableCell>{user.nome_completo}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  {user.role === "customer"
                    ? companies.find((c) => c.id === user.companyId)?.name || "Não atribuído"
                    : "It.up"}
                </TableCell>
                <TableCell align="center">
                  <IconButton color="primary" onClick={() => setSelectedUser(user)}>
                    <Edit sx={{ fontSize: 20 }} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ):null)}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de edição de usuário */}
      {selectedUser && (
        <Dialog open onClose={() => setSelectedUser(null)}>
          <DialogTitle>Editar usuário</DialogTitle>
          <DialogContent>
            <TextField
              label="Usuário"
              value={selectedUser.username}
              onChange={(e) => setSelectedUser({ ...selectedUser, username: e.target.value })}
              fullWidth
              margin="dense"
            />
            <TextField
              label="Nova Senha (opcional)"
              type="password"
              fullWidth
              margin="dense"
              value={selectedUser.newPassword || ""}
              onChange={(e) => setSelectedUser({ ...selectedUser, newPassword: e.target.value })}
            />

            <TextField
              label="Email"
              value={selectedUser.email}
              onChange={(e) => setSelectedUser({ ...selectedUser, email: e.target.value })}
              fullWidth
              margin="dense"
            />
            <TextField
              label="Nome Completo"
              fullWidth
              margin="dense"
              value={selectedUser.nome_completo}
              onChange={(e) => setSelectedUser({ ...selectedUser, nome_completo: e.target.value })}
            />
            <TextField
              label="Cargo"
              select
              value={selectedUser.role}
              onChange={(e) => setSelectedUser({ ...selectedUser, role: e.target.value })}
              fullWidth
              margin="dense"
            >
              <MenuItem value="user">Admin Secundário</MenuItem>
              <MenuItem value="admin">Administrador</MenuItem>
              <MenuItem value="customer">Admin Cliente</MenuItem>
            </TextField>

            <TextField
              label="Status"
              select
              value={selectedUser.is_enabled}
              onChange={(e) => setSelectedUser({ ...selectedUser, is_enabled: e.target.value })}
              fullWidth
              margin="dense"
            >
              <MenuItem value={1}>Ativo</MenuItem>
              <MenuItem value={0}>Desativado</MenuItem>
            </TextField>

            {selectedUser.role === "customer" && (
              <FormControl fullWidth margin="dense">
                <InputLabel>Empresa</InputLabel>
                <Select
                  value={selectedUser.companyId || ""}
                  onChange={(e) => setSelectedUser({ ...selectedUser, companyId: e.target.value })}
                >
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleUpdateUser} color="primary">
              Salvar
            </Button>
            <Button onClick={() => setSelectedUser(null)} color="secondary">
              Cancelar
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Dialog de exclusão */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: "bold", display: "flex", alignItems: "center", gap: 1 }}>
          <Delete color="error" /> Confirmar exclusão
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Tem certeza que deseja excluir o usuário <b>{userToDelete?.username}</b>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} variant="outlined" color="secondary">
            Cancelar
          </Button>
          <Button onClick={() => handleDeleteUser(userToDelete.id)} variant="contained" color="error">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>

      {/* Botão: abrir modal de cadastro rápido */}
      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button variant="contained" onClick={() => setRegisterDialogOpen(true)}>
          Cadastrar usuário
        </Button>
      </Box>

      {/* Popup de cadastro de usuário */}
      <UserRegisterDialog
        open={isRegisterDialogOpen}
        onClose={() => setRegisterDialogOpen(false)}
      />
    </Paper>
  );
};

export default UserManagement;
